import React from 'react';
import { SvgIcon } from '@mui/material';

/**
 * Icons for the Team Analysis, Set Pieces and Individual Actions buttons.
 *
 * Every icon draws in `currentColor`, so the attacking/defending colour comes
 * from the card rather than the icon. That is why the green and red buttons in a
 * pair - Attacking Corners and Defending Corners, Counter Attack and Counter
 * Defense - share one component instead of being drawn twice.
 *
 * All are 24x24 and stroke-based unless a solid shape reads better at 18px,
 * which is the size the cards render them at.
 */

const stroke = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.9,
    strokeLinecap: 'round',
    strokeLinejoin: 'round'
};

const solid = { fill: 'currentColor', stroke: 'none' };

const Icon = ({ children, ...props }) => (
    <SvgIcon viewBox="0 0 24 24" {...props}>
        {children}
    </SvgIcon>
);

/** 01 - All Attacking Possessions: a possession loop feeding an arrow forward. */
export const PossessionsIcon = (props) => (
    <Icon {...props}>
        <path {...stroke} d="M20.5 12a8.5 8.5 0 1 1-2.9-6.4" />
        <path {...stroke} d="M20.6 3.2v3.1h-3.1" />
        <path {...stroke} d="M8.4 12h6.2" />
        <path {...stroke} d="M12.4 9.8l2.2 2.2-2.2 2.2" />
    </Icon>
);

/** 02, 06 - Goalkeeper glove with the ball. */
export const GloveIcon = (props) => (
    <Icon {...props}>
        <path
            {...stroke}
            d="M7 20.5v-5.2L5.2 12a1.4 1.4 0 0 1 2.4-1.4l.9 1.5V4.6a1.3 1.3 0 0 1 2.6 0v5.2m0 0V3.6a1.3 1.3 0 0 1 2.6 0v6.2m0 0V5.1a1.3 1.3 0 0 1 2.6 0v8.2c0 3.6-1.4 5.6-3.2 7.2"
        />
        <circle {...stroke} cx="18.4" cy="18.4" r="3.1" />
        <path {...stroke} d="M16.6 16.1l3.6 4.6M20.2 16.1l-3.6 4.6" />
    </Icon>
);

/** 03, 05 - Possession phase: a plain shield. */
export const ShieldIcon = (props) => (
    <Icon {...props}>
        <path {...stroke} d="M12 2.8l7.6 2.9v6.1c0 4.9-3.2 8.6-7.6 10.4-4.4-1.8-7.6-5.5-7.6-10.4V5.7L12 2.8z" />
    </Icon>
);

/** 04 - Attack: the move progressing upfield. */
export const TrendUpIcon = (props) => (
    <Icon {...props}>
        <path {...stroke} d="M3.2 17.6l5.6-5.6 3.6 3.6 7.4-7.4" />
        <path {...stroke} d="M15.6 8.2h4.2v4.2" />
    </Icon>
);

/** 07 - High Press: the line stepping up together. */
export const DoubleUpIcon = (props) => (
    <Icon {...props}>
        <path {...stroke} d="M8.6 20.4V5.2m0 0L5.4 8.4M8.6 5.2l3.2 3.2" />
        <path {...stroke} d="M15.4 20.4V5.2m0 0l-3.2 3.2M15.4 5.2l3.2 3.2" />
    </Icon>
);

/** 08 - Defense: the shield with someone behind it. */
export const ShieldPersonIcon = (props) => (
    <Icon {...props}>
        <path {...stroke} d="M12 2.8l7.6 2.9v6.1c0 4.9-3.2 8.6-7.6 10.4-4.4-1.8-7.6-5.5-7.6-10.4V5.7L12 2.8z" />
        <circle {...solid} cx="12" cy="10" r="1.9" />
        <path {...solid} d="M8.4 16.8c0-2 1.6-3.4 3.6-3.4s3.6 1.4 3.6 3.4a9 9 0 0 1-3.6 2.6 9 9 0 0 1-3.6-2.6z" />
    </Icon>
);

/** 09, 12 - Transition: the turnover, a full cycle. */
export const TransitionRingIcon = (props) => (
    <Icon {...props}>
        <path {...stroke} d="M12 3.4a8.6 8.6 0 1 1-6.1 2.5" />
        <path {...stroke} d="M5.2 2.6l.7 3.5 3.5-.7" />
    </Icon>
);

/** 10, 13 - After a pass interception: the ball turned over in an instant. */
export const BoltIcon = (props) => (
    <Icon {...props}>
        <path {...solid} d="M13.9 1.8L4.6 13.4a.7.7 0 0 0 .55 1.14h4.2l-1.25 7.66a.42.42 0 0 0 .74.33l9.3-11.6a.7.7 0 0 0-.55-1.14h-4.2l1.25-7.66a.42.42 0 0 0-.74-.33z" />
    </Icon>
);

/** 11, 14 - After a 1v1: the player breaking away. */
export const RunnerIcon = (props) => (
    <Icon {...props}>
        <circle {...solid} cx="15.4" cy="4.3" r="2.1" />
        <path
            {...stroke}
            strokeWidth="2.1"
            d="M16.6 9.1l-3.7 1.6-2.2 3.6 2.9 2.1 1.2 5.1M12.9 10.7L8.4 8.9 4.6 11M12.9 14.3l-4.6 1.4-2.5 4.9"
        />
    </Icon>
);

/** 15, 18 - Corners: the corner flag and its quadrant. */
export const CornerFlagIcon = (props) => (
    <Icon {...props}>
        <path {...stroke} d="M7.2 3.1v17.8" />
        <path {...stroke} d="M7.2 3.9l9.6 3.2-9.6 3.2" />
        <path {...stroke} d="M3.2 20.9a9.4 9.4 0 0 1 9.4-9.4" />
    </Icon>
);

/** 16, 19 - Free kicks: the wall and the ball. */
export const WallIcon = (props) => (
    <Icon {...props}>
        <circle {...stroke} cx="7.6" cy="5.4" r="1.7" />
        <circle {...stroke} cx="12" cy="5.4" r="1.7" />
        <circle {...stroke} cx="16.4" cy="5.4" r="1.7" />
        <path {...stroke} d="M5.9 9.1h3.4v9.4H5.9zM10.3 9.1h3.4v9.4h-3.4zM14.7 9.1h3.4v9.4h-3.4z" />
        <circle {...stroke} cx="20.4" cy="19.7" r="2.3" />
    </Icon>
);

/** 17, 20 - Throw ins: the ball taken above the head. */
export const ThrowInIcon = (props) => (
    <Icon {...props}>
        <circle {...stroke} cx="12" cy="4.4" r="2.6" />
        <path {...stroke} d="M12 9.1v6.4M12 15.5l-2.9 6.1M12 15.5l2.9 6.1" />
        <path {...stroke} d="M12 9.6L7.4 6.1M12 9.6l4.6-3.5" />
    </Icon>
);

/** 21 - Goals: the ball crossing the line. */
export const GoalIcon = (props) => (
    <Icon {...props}>
        <path {...stroke} d="M2.9 19.4V7.2h18.2v12.2" />
        <path {...stroke} d="M2.9 13.3h18.2M9 7.2v12.2M15 7.2v12.2" />
        <circle {...solid} cx="17.4" cy="16.4" r="2.4" />
    </Icon>
);

/** 22 - Chances: the sight on goal. */
export const TargetIcon = (props) => (
    <Icon {...props}>
        <circle {...stroke} cx="12" cy="12" r="6.6" />
        <circle {...solid} cx="12" cy="12" r="2.1" />
        <path {...stroke} d="M12 1.6v3.4M12 19v3.4M1.6 12H5M19 12h3.4" />
    </Icon>
);

/** 23 - Offsides: the raised flag. */
export const OffsideFlagIcon = (props) => (
    <Icon {...props}>
        <path {...stroke} d="M6.4 2.9v18.2" />
        <path {...solid} d="M7.8 4.1h11.4v7.2H7.8z" />
    </Icon>
);

/** 24 - Saved: the keeper's hand. */
export const HandIcon = (props) => (
    <Icon {...props}>
        <path
            {...stroke}
            d="M7.4 21v-5.6l-1.9-3.4a1.45 1.45 0 0 1 2.5-1.45l1 1.7V4.4a1.35 1.35 0 0 1 2.7 0v5.4m0 0V3.4a1.35 1.35 0 0 1 2.7 0v6.4m0 0V4.9a1.35 1.35 0 0 1 2.7 0v9.1c0 4-1.8 5.9-3.6 7"
        />
    </Icon>
);

/** 25 - Blocked: the shot stopped. */
export const BlockedIcon = (props) => (
    <Icon {...props}>
        <circle {...stroke} cx="12" cy="12" r="8.8" />
        <path {...stroke} d="M5.8 5.8l12.4 12.4" />
    </Icon>
);

/** 26 - Clearance: the ball swept away. */
export const BroomIcon = (props) => (
    <Icon {...props}>
        <path {...stroke} d="M19.6 3.4l-8 8" />
        <path {...stroke} d="M12.7 9.1l-6.6 6.6 3.6 3.6 6.6-6.6z" />
        <path {...stroke} d="M6.1 15.7L2.8 21.2l5.5-3.3M11.6 21.2l-1.9-1.9M13.5 19.3l-1.9-1.9" />
    </Icon>
);

/** 27, 28 - The 1v1 duel, won or lost. */
export const OneVOneIcon = (props) => (
    <Icon {...props}>
        <rect {...stroke} x="2.4" y="5.6" width="19.2" height="12.8" rx="3.2" />
        <text
            x="12"
            y="15.7"
            textAnchor="middle"
            fontSize="8.4"
            fontWeight="700"
            fontFamily="'DM Sans', sans-serif"
            fill="currentColor"
            stroke="none"
        >
            1v1
        </text>
    </Icon>
);

/** Kick-Offs: the ball on the centre spot, the move about to start forward. */
export const KickOffIcon = (props) => (
    <Icon {...props}>
        <path {...stroke} d="M12 2.6v3.6" />
        <path {...stroke} d="M9.5 5.4L12 2.6l2.5 2.8" />
        <circle {...stroke} cx="12" cy="9.9" r="3.5" />
        <path {...stroke} strokeWidth="1.4" d="M12 8.4l1.43 1.04-.55 1.67h-1.76l-.55-1.67z" />
        <path {...stroke} strokeWidth="1.4" d="M12 8.4V6.4M13.43 9.44l1.9-.62M12.88 11.11l1.18 1.62M11.12 11.11l-1.18 1.62M10.57 9.44l-1.9-.62" />
        <ellipse {...stroke} cx="12" cy="16.4" rx="7.8" ry="3.2" />
        <path {...stroke} d="M12 13.4v6.8" />
    </Icon>
);
